import { useCallback, useState } from "react";
import { toast } from "sonner";
import { Header } from "@/components/Header";
import { Intro } from "@/components/Intro";
import { PinLogin } from "@/components/PinLogin";
import { RocketLaunch } from "@/components/RocketLaunch";
import { Capture } from "@/components/Capture";
import { Preview } from "@/components/Preview";
import { Generating } from "@/components/Generating";
import { Result } from "@/components/Result";
import { api, errorText, getAdminToken, setAdminToken } from "@/lib/api";

const afterIntro = () => (getAdminToken() ? "capture" : "login");

export default function Booth() {
  const [step, setStep] = useState(new URLSearchParams(window.location.search).has("skipIntro") ? afterIntro() : "intro");
  const [photo, setPhoto] = useState(null);
  const [creation, setCreation] = useState(null);
  const [options, setOptions] = useState({ theme: "clean", logo_placement: "corner", logo_mode: "event", print_style: "full", hashtag: false, label: "" });

  const finishIntro = useCallback(() => setStep((s) => (s === "intro" ? afterIntro() : s)), []);
  const finishLaunch = useCallback(() => setStep("capture"), []);

  const onCapture = (blob, previewUrl) => {
    setPhoto({ blob, previewUrl });
    setStep("preview");
  };

  const generate = async () => {
    setStep("generating");
    const form = new FormData();
    form.append("photo", photo.blob, "snap.jpg");
    form.append("theme", options.theme);
    form.append("logo_placement", options.logo_placement);
    form.append("hashtag", options.hashtag ? "true" : "false");
    form.append("label", options.label);
    form.append("logo_mode", options.logo_mode);
    try {
      const { data } = await api.post("/caricature", form, { timeout: 60000 });
      const started = Date.now();
      while (Date.now() - started < 240000) {
        await new Promise((r) => setTimeout(r, 2500));
        const { data: job } = await api.get(`/jobs/${data.job_id}`);
        if (job.status === "done") {
          setCreation(job.creation);
          setStep("result");
          return;
        }
        if (job.status === "error") throw new Error(job.error || "Karikatür üretilemedi");
      }
      throw new Error("Zaman aşımı, lütfen tekrar deneyin");
    } catch (e) {
      if (e?.response?.status === 401) {
        setAdminToken("");
        setStep("login");
      }
      toast.error(errorText(e, "Karikatür üretilemedi"));
      if (e?.response?.status !== 401) setStep("preview");
    }
  };

  const restart = () => {
    if (photo?.previewUrl) URL.revokeObjectURL(photo.previewUrl);
    setPhoto(null);
    setCreation(null);
    setStep("capture");
  };

  return (
    <div className="app-bg grain flex min-h-screen flex-col">
      {step === "intro" && <Intro onDone={finishIntro} />}
      {step === "launch" && <RocketLaunch onDone={finishLaunch} />}
      <Header />
      <main className="relative z-10 mx-auto flex w-full max-w-4xl flex-1 items-start justify-center px-4 py-8 pb-16 sm:px-6 sm:py-12" data-testid="booth-main">
        {step === "login" && (
          <PinLogin
            eyebrow="Birlikte Netiz"
            title="Kabine giriş"
            subtitle="Etkinlik PIN'i ile giriş yapın; ardından çekim başlar."
            onSuccess={() => setStep("launch")}
          />
        )}
        {step === "capture" && <Capture onCapture={onCapture} />}
        {step === "preview" && <Preview previewUrl={photo.previewUrl} options={options} onChange={setOptions} onRetake={restart} onGenerate={generate} />}
        {step === "generating" && <Generating />}
        {step === "result" && <Result creation={creation} initialVariant={options.print_style} onRestart={restart} />}
        {(step === "intro" || step === "launch") && <div className="h-40" />}
      </main>
    </div>
  );
}
